import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@rossum/api-client';
import { api } from '../../../../lib/apiClient';
import { neighborsKeys } from './queryKeys';

const NEIGHBOR_PREVIEW_STALE_TIME = 1000 * 60 * 60 * 24;

/**
 * Given an annotation ID and page number, fetches the page preview image.
 * First resolves the page ID via the pages list endpoint, then fetches the preview blob.
 */
export const useNeighborPagePreview = (
  annotationId: number | null,
  pageNumber: number
) => {
  const pageQuery = useQuery({
    queryKey: neighborsKeys.page(annotationId, pageNumber),
    queryFn: async () => {
      if (!annotationId) throw new Error('Missing annotation ID');
      const result = await api.request(
        endpoints.pages.list({
          annotation: annotationId,
          number: pageNumber,
          pageSize: 1,
        })
      );
      const page = result.results[0];
      if (!page) throw new Error('Page not found');
      return page;
    },
    enabled: !!annotationId,
    staleTime: NEIGHBOR_PREVIEW_STALE_TIME,
  });

  const pageId = pageQuery.data?.id;

  const previewQuery = useQuery({
    queryKey: neighborsKeys.pagePreview(pageId),
    queryFn: async () => {
      if (!pageId) throw new Error('Missing page ID');
      const response = await api.request(endpoints.pages.preview(pageId));
      return URL.createObjectURL(response);
    },
    enabled: !!pageId,
    staleTime: NEIGHBOR_PREVIEW_STALE_TIME,
  });

  return {
    previewUrl: previewQuery.data,
    pageWidth: pageQuery.data?.width ?? 0,
    pageHeight: pageQuery.data?.height ?? 0,
    // Use isInitialLoading (isFetching && isLoading) to avoid reporting loading
    // state for disabled queries (e.g. when annotationId is null).
    // In RQ v4, disabled queries have isLoading=true but isFetching=false.
    isLoading: pageQuery.isInitialLoading || previewQuery.isInitialLoading,
    isError: pageQuery.isError || previewQuery.isError,
  };
};
